import { useEffect, useState } from 'react'
import { mediaCache } from '../utils/mediaCache'

/**
 * Custom hook to load and cache media (images, videos) as Object URLs
 * This prevents redundant network requests for the same media file
 *
 * @param sourceUrl - The original media URL from the backend
 * @param fallback - Optional fallback URL to use while loading or on error
 * @returns The cached Object URL or fallback
 */
export const useCachedMedia = (sourceUrl: string | undefined, fallback?: string): string | undefined => {
  // Check synchronously if the media is already cached to avoid flicker
  const initialUrl = sourceUrl ? mediaCache.getCachedMediaUrlSync(sourceUrl) || fallback : fallback
  const [cachedUrl, setCachedUrl] = useState<string | undefined>(initialUrl)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    console.log('🎣 useCachedMedia useEffect triggered for:', sourceUrl)
    
    if (!sourceUrl) {
      setCachedUrl(fallback)
      return
    }

    let isMounted = true

    // Check if already cached synchronously first
    const syncCached = mediaCache.getCachedMediaUrlSync(sourceUrl)
    if (syncCached) {
      console.log('✨ useCachedMedia: Found in sync cache, skipping async fetch')
      setCachedUrl(syncCached)
      return () => {
        console.log('🧹 useCachedMedia cleanup (was cached):', sourceUrl)
        mediaCache.releaseCachedMediaUrl(sourceUrl)
      }
    }

    console.log('🚀 useCachedMedia: Not in sync cache, calling async getCachedMediaUrl')
    setIsLoading(true)

    mediaCache.getCachedMediaUrl(sourceUrl)
      .then(url => {
        if (isMounted) {
          console.log('📦 useCachedMedia: Got URL from async:', url ? 'Object URL' : 'fallback')
          setCachedUrl(url || fallback)
          setIsLoading(false)
        } else {
          console.log('⚠️ useCachedMedia: Component unmounted before async completed')
        }
      })
      .catch(error => {
        console.error('Error loading cached media:', error)
        if (isMounted) {
          // On error, use the original URL as fallback
          setCachedUrl(sourceUrl)
          setIsLoading(false)
        }
      })

    return () => {
      console.log('🧹 useCachedMedia cleanup (async fetch):', sourceUrl)
      isMounted = false
      mediaCache.releaseCachedMediaUrl(sourceUrl)
    }
  }, [sourceUrl, fallback])

  return cachedUrl
}

// Legacy export for backward compatibility
export const useCachedImage = useCachedMedia
